import {
  CapitalMovementSwitchingCreateDocument,
  ProductFundCreateDocument,
  ProductFundDeleteDocument,
  CapitalMovementSwitchingListDocument,
  ProductFundGetByIdDocument,
  ProductFundListDocument,
  ProductFundsPaginatedDocument,
  ProductFundUpdateDocument,
  VentureProductFundListDocument,
  ProductFundGetFundManagerDocument,
  QuantProductFundListDocument,
  ProductFundTypeListDocument,
  ProductFundCapitalTypeListDocument,
  ProductFundSortableField,
  OrderByDirectionEnum,
} from "api/generated";
import { ICommonListModel } from "api/services/types";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import {
  Nullable,
  Optional,
  PartialNullable,
  ResourceEnum,
  ScopeEnum,
} from "config";
import {
  NotificationDuration,
  NotificationMode,
  showSnackBar,
  store,
} from "store";

import {
  CreateFundArgsType,
  ICapitalMovementSwitchingModel,
  ICreateCapitalMovementSwitchingVars,
  IGetProductFundArgs,
  IGetProductFundListResponse,
  IProductFundModel,
  UpdateProductArgs,
} from "./types";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

export async function getProductFundList(
  variables?: IGetProductFundArgs
): Promise<IGetProductFundListResponse> {
  const makeRequest = getRequestInstance(RequestTypes.QUERY);
  const res = await makeRequest({
    query: ProductFundsPaginatedDocument,
    variables,
    fetchPolicy: "cache-first",
  });

  return res?.data?.productFundsPaginated;
}

export async function getProductFundTypeList(): Promise<ICommonListModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: ProductFundTypeListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.productFundTypeList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getProductFundCapitalTypeList(): Promise<
  ICommonListModel[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: ProductFundCapitalTypeListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.productFundCapitalTypeList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getProductFundListForFilters(): Promise<
  Omit<
    IProductFundModel,
    | "updated"
    | "updatedBy"
    | "updatedByName"
    | "created"
    | "createdBy"
    | "createdByName"
    | "dividendFrequency"
  >[]
> {
  const makeRequest = getRequestInstance(RequestTypes.QUERY);
  const res = await makeRequest({
    query: ProductFundsPaginatedDocument,
    fetchPolicy: "cache-first",
    variables: {
      paging: {
        limit: 1000,
        page: 0,
      },
      sorting: {
        orderBy: [
          {
            field: ProductFundSortableField.Name,
            direction: OrderByDirectionEnum.Asc,
          },
        ],
      },
    },
  });

  return res?.data?.productFundsPaginated?.edges?.map((el) => el.node) ?? [];
}

export async function createProductFund(
  variables: CreateFundArgsType
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ProductFundCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all product fund queries so they refetch with the new item
    executeEviction(client, {
      typename: "ProductFund",
      fieldNames: [
        "productFundsPaginated",
        "productFundList",
        "capitalFormationDashboardListProductFunds",
        "capitalFormationCommitmentTrackingPaginated",
      ],
    });

    return res?.data?.productFundCreate ?? false;
  } catch (error: any) {
    store.dispatch(
      showSnackBar({
        duration: NotificationDuration.ERRORS,
        message: error.message,
        mode: NotificationMode.ERROR,
      })
    );
    throw error;
  }
}

export async function getProductFundById(
  productFundId: number
): Promise<PartialNullable<IProductFundModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: ProductFundGetByIdDocument,
      variables: {
        productFundId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.productFundGetById;
  } catch (error) {
    throw error;
  }
}

export async function getProductFundManager(
  productFundId: number
): Promise<Optional<Nullable<number>>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: ProductFundGetFundManagerDocument,
      variables: {
        productFundId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.productFundGetFundManager;
  } catch (error) {
    throw error;
  }
}

export async function updateProductFund(
  variables: UpdateProductArgs
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ProductFundUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "ProductFund",
      id: variables.id.toString(),
      fieldNames: [
        "productFundsPaginated",
        "productFundList",
        "productFundGetById",
        "capitalFormationDashboardListProductFunds",
        "capitalFormationCommitmentTrackingPaginated",
      ],
    });

    return res?.data?.productFundUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deleteProductFund(
  productFundId: number
): Promise<boolean> {
  const makeRequest = getRequestInstance(RequestTypes.MUTATE);
  const res = await makeRequest({
    mutation: ProductFundDeleteDocument,
    variables: {
      productFundId,
    },
    fetchPolicy: "no-cache",
  });

  // Evict the deleted entity and all related data
  executeEviction(client, {
    typename: "ProductFund",
    id: productFundId.toString(),
    fieldNames: [
      "productFundsPaginated",
      "productFundList",
      "productFundGetById",
      "capitalFormationDashboardListProductFunds",
      "capitalFormationCommitmentTrackingPaginated",
    ],
  });

  return Boolean(res?.data?.productFundDelete);
}

export async function getProductFundsName(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<
  PartialNullable<Pick<IProductFundModel, "name" | "id" | "sharePrice">>[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: ProductFundListDocument,
      variables: {
        resource,
        scope,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.productFundList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function createCapitalMovementSwitching(
  variables: ICreateCapitalMovementSwitchingVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: CapitalMovementSwitchingCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    return Boolean(res?.data?.capitalMovementSwitchingCreate);
  } catch (error) {
    throw error;
  }
}

export async function getCapitalMovementSwitching(
  productFundId: number
): Promise<ICapitalMovementSwitchingModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: CapitalMovementSwitchingListDocument,
      variables: { productFundId },
      fetchPolicy: "cache-first",
    });

    return res?.data?.capitalMovementSwitchingList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getVentureProductFunds(): Promise<
  PartialNullable<Pick<IProductFundModel, "name" | "id" | "sharePrice">>[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureProductFundListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureProductFundList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getQuantProductFunds(): Promise<
  PartialNullable<Pick<IProductFundModel, "name" | "id" | "sharePrice">>[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: QuantProductFundListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.quantProductFundList ?? [];
  } catch (error) {
    throw error;
  }
}
